/**
 * Screen 11 — 砺锋台中转页（V8 · 2026-07-20）
 *
 * 从首页/关卡列表 → 砺锋台的强制中转页：
 *   - 不可跳过（必须看完整个动画才进入 #/sharpen）
 *   - 剑从鞘中缓慢拔出 + 鞘口/剑尖火花粒子
 *   - 8 行绝句「铸剑行」逐行打字机效果
 *   - 动画结束自动跳转到砺锋台
 *
 * 路由：#/sharpen-transition
 * 动画总时长：~14s（fadeIn 0.6s + draw 8s + sword 10.5s + hold/fade 3s）
 *
 * 素材（public/sprites/sharpen-transition/）：
 *   scabbard.png  鞘（1080x72，去白底，RGBA 透明）
 *   sword.png     剑（1080x79，去白底，RGBA 透明）
 *   bg.png        深蓝纹理底图（1080x603）
 *   overlay.png   HUD 叠加图（半透明蓝）
 *
 * 视觉布局：
 *   - 横向三等分，剑横骑在下 1/3 分界线上（y = H * 0.66）
 *   - 鞘在中央 1/3 区域固定不动
 *   - 剑从鞘口（鞘右端）向左抽出
 *   - 8 行绝句在上 2/3 区域居中排列
 *   - 绝句顶部"铸剑行"小标题
 *
 * 与 V8 屏 12 铸剑阁（#/forge）的关系：完全独立的过渡屏，仅服务于砺锋台入口。
 */

import { pathFor } from "../router/screen-router";

// ===== 8 行绝句·铸剑行（绝句+律诗融合,8 行 × 7 字） =====
// V8.1: 律诗「双壁对」传统 → 左列 4 行 / 右列 4 行
// V14.0: 去掉标题括号
// const POEM_TITLE_BRACKET_OPEN = "[";
// const POEM_TITLE_BRACKET_CLOSE = "]";
const POEM_TITLE_TEXT = "铸剑行";
const POEM_TITLE_EN = "FORGE.PROTOCOL // 0x11";
const POEM_LINES_LEFT = [
  "承颅遗志铸新锋，",
  "案头兵书墨未干。",
  "纸上锋芒先磨砺，",
  "青锋已在匣中鸣。",
];
const POEM_LINES_RIGHT = [
  "代码行行入剑中。",
  "炉前铁火已经年。",
  "编译如淬千度火，",
  "出阁之日即擎天。",
];
// 合并为单一时序数组（用于打字机调度：按 i=0..7 顺序）
const POEM_LINES = [
  ...POEM_LINES_LEFT,
  ...POEM_LINES_RIGHT,
];
// ===== 动画时序常量（毫秒） =====
const T = {
  fadeIn: 700,         // 屏淡入
  drawStart: 800,      // 第 1 行绝句开始打字
  drawLineDur: 950,    // 每行 7 字 × 130ms = 910ms，凑整 950
  drawLineGap: 100,    // 行间停顿
  swordStart: 1000,    // 剑开始抽出
  swordEnd: 12500,     // 剑完全抽出
  fadeOutStart: 14000, // 淡出开始
  fadeOutDur: 900,     // 淡出时长
  totalHold: 15100,    // 全部结束+跳转到 #/sharpen
};

// ===== 粒子 =====
interface Spark {
  x: number; y: number;
  vx: number; vy: number;
  life: number; maxLife: number;
  color: string; size: number;
}
const SPARK_COLORS = ["#FFE9B5", "#FFCB69", "#7FE0FF", "#E8F4FF", "#FFD27A", "#FFC85C"];

export function renderScreen11SharpenTransition(
  container: HTMLElement,
  onNavigate: (path: string) => void
): void {
  container.innerHTML = `
    <div class="s11-transition" id="s11-root">
      <div class="s11-bg"></div>
      <div class="s11-overlay"></div>
      <div class="s11-poem" id="s11-poem">
        <h1 class="s11-poem-title">
          <span class="s11-poem-title__text">${POEM_TITLE_TEXT}</span>
          <span class="s11-poem-title__en">${POEM_TITLE_EN}</span>
        </h1>
        <div class="s11-poem-grid">
          <div class="s11-poem-col s11-poem-col--left" id="s11-poem-col-left"></div>
          <div class="s11-poem-col s11-poem-col--right" id="s11-poem-col-right"></div>
        </div>
      </div>
      <canvas class="s11-canvas" id="s11-canvas"></canvas>
      <div class="s11-corner s11-corner-tl"></div>
      <div class="s11-corner s11-corner-tr"></div>
      <div class="s11-corner s11-corner-bl"></div>
      <div class="s11-corner s11-corner-br"></div>
      <div class="s11-status" id="s11-status">// BLADE.PROTOCOL // 0x11</div>
    </div>
  `;

  const root = container.querySelector("#s11-root") as HTMLElement;
  const canvas = container.querySelector("#s11-canvas") as HTMLCanvasElement;
  const poemGrid = container.querySelector(".s11-poem-grid") as HTMLElement;
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) {
    console.error("[s11] canvas context null");
    return;
  }
  if (!poemGrid) {
    console.error("[s11] poemGrid element not found");
    return;
  }

  // 加载素材
  const scabbardImg = new Image();
  const swordImg = new Image();
  scabbardImg.src = "/sprites/sharpen-transition/scabbard.png";
  swordImg.src = "/sprites/sharpen-transition/sword.png";

  // ===== 屏尺寸 =====
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  let W = 0, H = 0;
  function resize() {
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  resize();

  // ===== 文字 DOM：V9.0 直接 grid 子项（2×4 矩阵） =====
  // 改变策略：不用左右两个容器，而是直接构建 grid 子项
  // grid 2 列，8 行 = 左列 4 行 + 右列 4 行
  const lineEls: HTMLElement[] = [];  // lineEls[i] = 第 i 行 div
  
  POEM_LINES.forEach((_, i) => {
    const lineEl = document.createElement("div");
    lineEl.className = "s11-poem-line";
    lineEl.dataset.lineIndex = String(i);
    lineEl.textContent = ""; // 初始化为空
    
    // 行 i：前 4 行放左列，后 4 行放右列
    // grid 会自动按顺序 fill-row 填充（第 0-3 行左列，第 4-7 行右列）
    poemGrid.appendChild(lineEl);
    lineEls.push(lineEl);
  });

  // ===== 粒子系统 =====
  const sparks: Spark[] = [];
  function spawnSpark(emitX: number, emitY: number, intensity: number = 1) {
    const baseAngle = Math.PI * 0.5; // 向上
    const spread = Math.PI * 0.8;     // 散开 144°
    const angle = baseAngle + (Math.random() - 0.5) * spread;
    const speed = (0.5 + Math.random() * 1.4) * intensity;
    const life = 28 + Math.random() * 38; // 0.45-1.1s @ 60fps
    sparks.push({
      x: emitX + (Math.random() - 0.5) * 6,
      y: emitY + (Math.random() - 0.5) * 3,
      vx: Math.cos(angle) * speed * 0.6,
      vy: -Math.abs(Math.sin(angle)) * speed,
      life,
      maxLife: life,
      color: SPARK_COLORS[Math.floor(Math.random() * SPARK_COLORS.length)],
      size: 0.7 + Math.random() * 1.6,
    });
  }

  // ===== 主循环 =====
  const startTime = performance.now();
  let rafId = 0;
  let navigated = false;

  function dispose() {
    cancelAnimationFrame(rafId);
    window.removeEventListener("resize", resize);
  }

  function navigateTo() {
    if (navigated) return;
    navigated = true;
    dispose();
    onNavigate(pathFor("sharpen"));
  }

  function update() {
    const elapsed = performance.now() - startTime;

    // ----- 文字打字机（V8.3 逐字追加 textContent，参考首页实现） -----
    // 每字间隔 60ms
    const CHAR_DUR = 60;
    
    for (let i = 0; i < POEM_LINES.length; i++) {
      const lineStart = T.drawStart + i * (T.drawLineDur + T.drawLineGap);
      const line = POEM_LINES[i];
      const lineEl = lineEls[i];
      
      // 计算这一行应该显示到第几个字
      const timeSinceLineStart = Math.max(0, elapsed - lineStart);
      const charsToShow = Math.floor(timeSinceLineStart / CHAR_DUR);
      
      if (charsToShow > 0 && charsToShow <= line.length) {
        // 从第 0 字到第 charsToShow-1 字
        lineEl.textContent = line.substring(0, charsToShow);
      } else if (charsToShow > line.length) {
        // 该行已完全显示
        lineEl.textContent = line;
      } else {
        // 还未到这一行打字时间
        lineEl.textContent = "";
      }
    }

    // ----- Canvas 剑 + 鞘 + 粒子 -----
    ctx!.clearRect(0, 0, W, H);

    // ===== V9.0 剑鞘修复：鞘口屏幕中右 + 向右拔出 + 放大 5 倍 =====
    // 目标方向：鞘口在中央，剑从鞘口向**右侧**拔出（剑尖朝右）
    // 源图分析：scabbard.png 1080x72
    //   - 鞘长 724px，布局：97..821
    //   - 原图：鞘口(右) 821 → 鞘底(左) 97
    //   - 无翻转：鞘口朝右（原始方向）
    // 放大：0.70W（相对原来 ~5 倍）
    const SCABBARD_SOURCE_LEFT = 97;
    const SCABBARD_SOURCE_RIGHT = 821;
    const SCABBARD_SOURCE_W = SCABBARD_SOURCE_RIGHT - SCABBARD_SOURCE_LEFT;  // 724
    const SCABBARD_SOURCE_H = 72;
    const P_MAX = 0.80;  // 限制剑抽出比例防止剑尖飞出屏右

    const scabbardW = Math.min(W * 0.70, 900);  // V9.0: 0.70W
    const scabbardH = scabbardW * (SCABBARD_SOURCE_H / SCABBARD_SOURCE_W);
    // V13.0: 鞘向左移 1/3 屏宽，向下移到下边缘画面 1/4 位置，再下移 100px
    // V15.0: 上移 100px（从 +100 改为 0）
    const scabbardX = (W - scabbardW) / 2 - W / 3;
    const scabbardY = H * 0.75 - scabbardH / 2;
    // 鞘口 = 鞘的右端（原始方向，鞘口在右，指向右侧）
    const mouthX = scabbardX + scabbardW;
    const mouthY = scabbardY + scabbardH / 2;

    // 剑抽出进度（缓动 + P_MAX 限制）
    // V16.0: 加强动效，缓动曲线改为 easeOutQuad（快速开始 → 减速结束），更明显
    const swordRaw = Math.min(1, Math.max(0, (elapsed - T.swordStart) / (T.swordEnd - T.swordStart)));
    const swordP = easeOutQuad(swordRaw) * P_MAX;

    // ===== V9.0 剑水平无翻转 + 鞘口朝右 + 向右拔出 =====
    // 源图分析：sword.png 1080x79,剑身 x=73..1015(942px)
    //          剑尖在源图左(x=73),剑柄在源图右(x=1015)
    // 目标：无翻转，剑尖朝右侧拔出
    //   - 剑柄在鞘内（鞘底附近 = 鞘左侧）
    //   - 剑尖在鞘外（鞘口右侧 = 屏幕右侧）
    const SWORD_BODY_LEN = 942;          // 实际剑身长
    const SWORD_IMG_H = 79;              // 源图总高
    const SWORD_SRC_X = 73;              // 源图剑身起始 x（无翻转）

    // 视觉：剑高 = 鞘高
    const swordH = scabbardH;
    const swordScale = swordH / SWORD_IMG_H;
    const swordFullW = SWORD_BODY_LEN * swordScale; // 完全抽出时剑身显示宽
    // V13.0: 剑与鞘在同一水平线上（同步移动）
    const swordY = scabbardY;

    // V11.0 剑位移动画：剑初始完全在鞘内，向右拔出
    // 源图分析：sword.png 1080x79，剑身 x=73..1015(942px)，无翻转
    // 动画：
    //   - swordP=0: 剑完全在鞘内，剑尖在鞘口附近（看不见）
    //   - swordP=1: 剑完全拔出，剑尖在屏幕右侧
    if (swordImg.complete && swordImg.naturalWidth > 0) {
      // 初始状态：剑柄在鞘左端，剑尖在鞘口（完全隐藏在鞘内）
      const initialSwordX = scabbardX + 100;  // V13.1: 向右偏移 100px
      // 拔出动画：剑向右移动 (剑柄位置) 从 scabbardX → mouthX
      // 拔出距离 = 鞘口 - 鞘底 = scabbardW
      const swordMoveDistance = scabbardW;
      const currentSwordX = initialSwordX + swordMoveDistance * swordP;
      
      // V17.0: 剑描边特效（处理抠图毛刺）
      // 绘制 3 层发光描边（由暗到亮）
      for (let glowR = 8; glowR > 0; glowR -= 2) {
        const glowAlpha = (3 - glowR / 2) * 0.08;  // 递减透明度
        ctx!.globalAlpha = glowAlpha;
        ctx!.filter = `blur(${glowR}px)`;
        ctx!.drawImage(
          swordImg,
          SWORD_SRC_X, 0, SWORD_BODY_LEN, SWORD_IMG_H,
          currentSwordX, swordY, swordFullW, swordH
        );
      }
      ctx!.globalAlpha = 1;
      ctx!.filter = "none";
      
      // 绘制完整剑身（主体，覆盖发光层）
      ctx!.drawImage(
        swordImg,
        SWORD_SRC_X, 0, SWORD_BODY_LEN, SWORD_IMG_H,  // source: 完整剑身
        currentSwordX, swordY, swordFullW, swordH      // dest: 位移后的位置
      );

      // 火花固定在鞘口附近（不跟剑尖移动）
      // 火花应该表现"剑在鞘口摩擦"的效果，位置固定
      if (swordP > 0.01 && swordP < 1.0) {
        const intensity = Math.sin(swordP * Math.PI) * 1.5;
        // 鞘口上方火花（模拟摩擦）
        if (Math.random() < 0.70) spawnSpark(mouthX + 10, mouthY - 15, intensity);
        // 鞘口下方火花
        if (Math.random() < 0.70) spawnSpark(mouthX + 10, mouthY + 15, intensity);
        // 鞘口中心火花
        if (Math.random() < 0.50) spawnSpark(mouthX, mouthY, intensity * 0.8);
      }
    }

    // 鞘（后画,覆盖剑的鞘内部分 → 视觉上"剑在鞘内"被遮住）
    // V12.0: 鞘水平翻转（左右镜像）
    // 源图：x=97..821（鞘长 724px）
    if (scabbardImg.complete && scabbardImg.naturalWidth > 0) {
      ctx!.save();
      const centerX = scabbardX + scabbardW / 2;
      const centerY = scabbardY + scabbardH / 2;
      ctx!.translate(centerX, centerY);
      ctx!.scale(-1, 1);
      ctx!.translate(-centerX, -centerY);
      
      // V17.0: 鞘描边特效（处理抠图毛刺）
      // 绘制 3 层发光描边（由暗到亮）
      for (let glowR = 8; glowR > 0; glowR -= 2) {
        const glowAlpha = (3 - glowR / 2) * 0.08;
        ctx!.globalAlpha = glowAlpha;
        ctx!.filter = `blur(${glowR}px)`;
        ctx!.drawImage(
          scabbardImg,
          SCABBARD_SOURCE_LEFT, 0, SCABBARD_SOURCE_W, SCABBARD_SOURCE_H,
          scabbardX, scabbardY, scabbardW, scabbardH
        );
      }
      ctx!.globalAlpha = 1;
      ctx!.filter = "none";
      
      // 绘制鞘主体（覆盖发光层）
      ctx!.drawImage(
        scabbardImg,
        SCABBARD_SOURCE_LEFT, 0, SCABBARD_SOURCE_W, SCABBARD_SOURCE_H,
        scabbardX, scabbardY, scabbardW, scabbardH
      );
      
      ctx!.restore();
    }

    // 粒子渲染
    for (let i = sparks.length - 1; i >= 0; i--) {
      const p = sparks[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.025;       // 弱重力
      p.vx *= 0.985;
      p.vy *= 0.992;
      p.life--;
      const ratio = Math.max(0, p.life / p.maxLife);
      const alpha = Math.pow(ratio, 0.7);
      // 主光点
      ctx!.fillStyle = p.color + hexAlpha(alpha * 0.95);
      ctx!.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
      // 外发光（小半径 + 大半径）
      if (p.size > 1.2) {
        ctx!.fillStyle = p.color + hexAlpha(alpha * 0.25);
        ctx!.fillRect(p.x - p.size, p.y - p.size, p.size * 2, p.size * 2);
      }
      if (p.life <= 0) sparks.splice(i, 1);
    }

    // 屏淡入/淡出
    let fade = 1;
    if (elapsed < T.fadeIn) {
      fade = elapsed / T.fadeIn;
    } else if (elapsed >= T.fadeOutStart) {
      fade = Math.max(0, 1 - (elapsed - T.fadeOutStart) / T.fadeOutDur);
    }
    root.style.setProperty("--s11-fade", String(fade));

    // 跳转
    if (elapsed >= T.totalHold) {
      navigateTo();
      return;
    }
    rafId = requestAnimationFrame(update);
  }

  rafId = requestAnimationFrame(update);
}

// V16.0: 快速开始 → 减速结束（加强拔剑动效）
function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

function hexAlpha(a: number): string {
  return Math.round(Math.max(0, Math.min(1, a)) * 255)
    .toString(16)
    .padStart(2, "0");
}
